import {
  ensureContrast,
  rgbaObjToStr,
  unknownColorTypeStrToRgbaObj,
} from '@ardoq/color-helpers';
import {
  ChartWindowState,
  TimeRange,
  TimeScalingFunction,
  getFullComponentLabelWithLegacyFormatting,
  rect,
  timeScaler,
  truncateComponentLabel,
} from '@ardoq/graph';
import {
  CONTEXT_ITEM_BACKGROUND_COLOR,
  LABELS_COLUMN_BACKGROUND_COLOR,
  TIMELINE_INFINITE_PHASE_INDICATOR_WIDTH,
  TIMELINE_MILESTONE_SIZE,
  TIMELINE_INDIVIDUAL_ITEM_MARGIN,
  TIMELINE_MODERNIZED_GROUP_INDENTATION,
  TIMELINE_MODERNIZED_COMPONENT_INDENTATION,
  ICON_WIDTH,
  ICON_MARGIN,
  COLOR_INDICATOR_WIDTH,
  TIMELINE_FIELD_DATA_EXPANDER_WIDTH,
  TIMELINE_LABEL_CONTAINER_PADDING_X,
} from './consts';
import {
  TimelineItemModel,
  TimelineLabelWithRenderingParameters,
  TimelinePhasesWithRenderingParameters,
  TimelinePhase,
  RelevantCompontsScopeDataMap,
  Milestone,
  ItemPhasesRow,
} from '../types';
import { isTruthy } from '@ardoq/common-helpers';
import { getDynamicPhaseStyles } from './phases/calculations';
import { getGroupSeparatorStyle } from '../chart/calculations';
import {
  measureGridLabelBoldTextWidth,
  measureGridLabelTextWidth,
} from '../utils';
import { getNegativeLabelSpaceForLabelsColumn } from '../data/calculations';
import { findIndex } from 'lodash';
import { getOtherLabelsRenderingData } from './labels/otherLabelsRenderingData';
import { colors } from '@ardoq/design-tokens';

type GetPhasesScalerArgs = ChartWindowState & {
  chartWidth: number;
  availableExpandedDomain: TimeRange;
};
export const getPhasesScaler = ({
  chartWidth,
  windowScale,
  windowCenter,
  availableExpandedDomain,
}: GetPhasesScalerArgs) => {
  const { left, right } = rect(windowCenter, windowScale);
  return timeScaler([0, chartWidth], availableExpandedDomain, [left, right]);
};

const getIsLastChild = (
  item: TimelineItemModel,
  items: TimelineItemModel[],
  index: number
) => {
  const { treeDepth } = item;

  if (!treeDepth) {
    return false;
  }

  const lastItemIndex = items.length - 1;
  const nextItemIndex = index + 1;

  if (index === lastItemIndex) {
    return true;
  }

  // groups below can have children on the same depth, but we don't care about them
  // we only care about nodes in this subtree on this tree depth level.
  // That means:
  // our search boundaries is from the next item to the next item with a smaller tree depth (or end of list)
  // if we find any item on the same tree depth level in this range, then we are not the last child
  // if we don't find any, then we are the last child

  // find the next item with smaller tree depth (next parent level group)
  const indexOfNextParentLevelGroup = findIndex(
    items,
    item => item.treeDepth < treeDepth,
    nextItemIndex
  );

  // if there is none, we search until the end of the list
  const searchBoundaryIndex =
    indexOfNextParentLevelGroup === -1
      ? lastItemIndex + 1
      : indexOfNextParentLevelGroup;

  const siblingAndDescendantItems = items.slice(
    nextItemIndex,
    searchBoundaryIndex
  );

  return siblingAndDescendantItems.every(item => item.treeDepth > treeDepth);
};

type BuildItemLabelRenderingDataArgs = {
  items: TimelineItemModel[];
  item: TimelineItemModel;
  index: number;
  labelsColumnWidth: number;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  hasSiblingGroup: boolean;
};
const buildItemLabelRenderingData = ({
  item,
  items,
  index,
  labelsColumnWidth,
  viewRelevantComponentsScopeData,
  hasSiblingGroup,
}: BuildItemLabelRenderingDataArgs): TimelineLabelWithRenderingParameters => {
  const { modelId, treeDepth, isGroup, itemLabels } = item;

  const { mainLabel, legacyLabelParts, subLabel, otherLabels } = itemLabels;

  const subLabelWidth = subLabel
    ? measureGridLabelBoldTextWidth(` (${subLabel})`)
    : 0;

  const measure = isGroup
    ? measureGridLabelBoldTextWidth
    : measureGridLabelTextWidth;

  const availableSpace =
    labelsColumnWidth -
    getNegativeLabelSpaceForLabelsColumn(item, hasSiblingGroup);
  const labelAvailableSpace = availableSpace - subLabelWidth;

  const truncatedLabel =
    // legacyLabelParts is only defined if view is not modernized style
    // in modernized style we don't need formatting that truncateComponentLabel provides
    labelAvailableSpace && legacyLabelParts
      ? truncateComponentLabel({
          ...legacyLabelParts,
          measure,
          width: labelAvailableSpace,
        })
      : mainLabel;

  const isLabelTruncated = legacyLabelParts
    ? getFullComponentLabelWithLegacyFormatting(legacyLabelParts) !==
      truncatedLabel
    : measure(mainLabel || '') >
      labelAvailableSpace - ICON_MARGIN - TIMELINE_LABEL_CONTAINER_PADDING_X;

  const popoverContent = isLabelTruncated
    ? {
        mainLabel,
        subLabel,
      }
    : undefined;

  const otherLabelsAvailableSpace =
    availableSpace +
    (TIMELINE_FIELD_DATA_EXPANDER_WIDTH -
      TIMELINE_LABEL_CONTAINER_PADDING_X * 2);

  const otherLabelsRenderingData = getOtherLabelsRenderingData({
    otherLabels,
    availableSpace: otherLabelsAvailableSpace,
  });

  const componentScopeData = viewRelevantComponentsScopeData.get(modelId);

  const componentColor = componentScopeData?.componentColor || colors.black950;
  const contrastColor = ensureContrast(
    LABELS_COLUMN_BACKGROUND_COLOR,
    componentColor
  );

  const parentOrPreviousSibling =
    treeDepth && index > 0 ? items[index - 1] : null;

  const isFirstChild =
    treeDepth && parentOrPreviousSibling
      ? parentOrPreviousSibling.treeDepth < treeDepth
      : false;

  const isLastChild = getIsLastChild(item, items, index);

  const showGroupSeparator =
    Boolean(treeDepth) && items[index + 1]?.treeDepth === 0;

  const groupSeparatorStyle = getGroupSeparatorStyle(true, showGroupSeparator);

  const siblingGroupMargin =
    treeDepth === 0
      ? TIMELINE_INDIVIDUAL_ITEM_MARGIN
      : TIMELINE_INDIVIDUAL_ITEM_MARGIN +
        TIMELINE_MODERNIZED_GROUP_INDENTATION -
        TIMELINE_MODERNIZED_COMPONENT_INDENTATION;

  const labelContentStyle =
    hasSiblingGroup && !isGroup
      ? { marginLeft: siblingGroupMargin }
      : undefined;

  const colorIndicatorWidth = isGroup ? 0 : COLOR_INDICATOR_WIDTH;

  const otherLabelsContainerStyle = {
    paddingLeft: ICON_WIDTH + ICON_MARGIN * 3 + colorIndicatorWidth,
  };

  return {
    itemModel: {
      ...item,
      // first, the main label is not truncated at all, as we don't know the available space
      // during the data composition. Here we replace the main label with the truncated version.
      itemLabels: {
        ...itemLabels,
        mainLabel: truncatedLabel,
      },
    },
    otherLabelsRenderingData,
    popoverContent,
    isFirstChild,
    isLastChild,
    otherLabelsContainerStyle,
    groupSeparatorStyle,
    labelContentStyle,
    isComponentHierarchyCollapsed: false,
    isFieldDataCollapsed: !item.isFieldDataCollapsible,
    componentRepresentationData: componentScopeData
      ? componentScopeData.representationData
      : null,
    contrastedComponentColor: contrastColor,
    colorIndicatorColor: componentColor,
  };
};

/**
 * Builds a set of item indices where the item is a non-group that has at least one
 * group sibling (a sibling with an expand toggle). These items need extra left margin
 * so their text aligns with the text of their group siblings.
 */
const buildHasSiblingGroupSet = (items: TimelineItemModel[]): Set<number> => {
  const parentIndexOf = new Array<number>(items.length).fill(-1);
  const lastSeenIndexAtDepth: number[] = [];

  for (let i = 0; i < items.length; i++) {
    const { treeDepth } = items[i];
    if (treeDepth > 0) {
      parentIndexOf[i] = lastSeenIndexAtDepth[treeDepth - 1] ?? -1;
    }
    lastSeenIndexAtDepth[treeDepth] = i;
  }

  const siblingsByParent = new Map<number, number[]>();
  for (let i = 0; i < items.length; i++) {
    const parent = parentIndexOf[i];
    const siblings = siblingsByParent.get(parent);
    if (siblings) {
      siblings.push(i);
    } else {
      siblingsByParent.set(parent, [i]);
    }
  }

  const hasSiblingGroupSet = new Set<number>();
  siblingsByParent.forEach(siblings => {
    if (siblings.some(idx => items[idx].isGroup && items[idx].rawItemIdPath)) {
      siblings.forEach(idx => {
        if (!items[idx].isGroup) hasSiblingGroupSet.add(idx);
      });
    }
  });

  return hasSiblingGroupSet;
};

type GetLabelsWithrenderingParametersArgs = {
  items: TimelineItemModel[];
  labelsColumnWidth: number;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
};
export const getLabelsWithRenderingParameters = ({
  items,
  labelsColumnWidth,
  viewRelevantComponentsScopeData,
}: GetLabelsWithrenderingParametersArgs): TimelineLabelWithRenderingParameters[] => {
  const hasSiblingGroupSet = buildHasSiblingGroupSet(items);
  return items.map((item, index, items) =>
    buildItemLabelRenderingData({
      items,
      item,
      index,
      labelsColumnWidth,
      viewRelevantComponentsScopeData,
      hasSiblingGroup: hasSiblingGroupSet.has(index),
    })
  );
};

const enhanceTimelinePhaseWithRenderingData = (
  phase: TimelinePhase,
  scaleX: TimeScalingFunction,
  visibleDomain: TimeRange,
  isModernizedStyle: boolean,
  phaseColor: string
) => {
  const [visibleDomainStart, visibleDomainEnd] = visibleDomain;

  const { start: phaseStart, end: phaseEnd } = phase;

  if (
    (!phaseEnd && !phaseStart) ||
    (phaseStart && phaseStart > visibleDomainEnd) ||
    (phaseEnd && phaseEnd < visibleDomainStart)
  ) {
    return null;
  }

  const displayedStart = phaseStart || visibleDomainStart;
  const displayedEnd = phaseEnd || visibleDomainEnd;
  const infiniteLeft = phaseStart === null;
  const infiniteRight = phaseEnd === null;

  const scaledStart =
    scaleX(displayedStart) +
    (infiniteLeft ? TIMELINE_INFINITE_PHASE_INDICATOR_WIDTH : 0);

  const scaledEnd =
    scaleX(displayedEnd) -
    (infiniteRight ? TIMELINE_INFINITE_PHASE_INDICATOR_WIDTH : 0);

  return {
    ...phase,
    scaledStart,
    scaledEnd,
    dynamicPhaseStyles: getDynamicPhaseStyles({
      isModernizedStyle,
      infiniteLeft,
      infiniteRight,
      scaledStart,
      scaledEnd,
      color: phaseColor,
    }),
    infiniteLeft,
    infiniteRight,
  };
};

const enhanceMilestoneWithRenderingData = (
  milestone: Milestone,
  scaleX: TimeScalingFunction
) => ({
  ...milestone,
  scaledStart: scaleX(milestone.date) - TIMELINE_MILESTONE_SIZE / 2, // center of the icon at scaled start
});

const enhanceTimelinePhasesRowWithRenderingData = (
  phasesRow: ItemPhasesRow,
  scaleX: TimeScalingFunction,
  visibleDomain: TimeRange,
  isModernizedStyle: boolean,
  phaseColor: string
) => ({
  ...phasesRow,
  phases: phasesRow.phases
    .map(phase =>
      enhanceTimelinePhaseWithRenderingData(
        phase,
        scaleX,
        visibleDomain,
        isModernizedStyle,
        phaseColor
      )
    )
    .filter(isTruthy),
  milestones: phasesRow.milestones.map(milestone =>
    enhanceMilestoneWithRenderingData(milestone, scaleX)
  ),
});

type BuildItemPhasesRenderingDataArgs = {
  items: TimelineItemModel[];
  scaleX: TimeScalingFunction;
  visibleDomain: TimeRange;
  item: TimelineItemModel;
  index: number;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  isModernizedStyle: boolean;
};
const buildItemPhasesRenderingData = ({
  item,
  items,
  index,
  visibleDomain,
  scaleX,
  viewRelevantComponentsScopeData,
  isModernizedStyle,
}: BuildItemPhasesRenderingDataArgs): TimelinePhasesWithRenderingParameters => {
  const {
    modelId,
    expandedItemPhasesRows,
    collapsedItemPhasesRows,
    treeDepth,
  } = item;

  const componentscopeData = viewRelevantComponentsScopeData.get(modelId);

  const componentColor = componentscopeData?.componentColor || colors.black950;

  const phaseColor = rgbaObjToStr({
    ...unknownColorTypeStrToRgbaObj(componentColor),
    alpha: 0.6,
  });

  const showGroupSeparator =
    Boolean(treeDepth) && items[index + 1]?.treeDepth === 0;

  const groupSeparatorStyle = getGroupSeparatorStyle(false, showGroupSeparator);

  const enhanceWithRenderingData = (phasesRow: ItemPhasesRow) =>
    enhanceTimelinePhasesRowWithRenderingData(
      phasesRow,
      scaleX,
      visibleDomain,
      isModernizedStyle,
      phaseColor
    );

  return {
    itemModel: item,
    groupSeparatorStyle,
    backgroundColor: item.isContext
      ? CONTEXT_ITEM_BACKGROUND_COLOR
      : 'transparent',
    collapsedPhasesRowsWithRenderingParameters: collapsedItemPhasesRows.map(
      enhanceWithRenderingData
    ),
    expandedPhasesRowsWithRenderingParameters: expandedItemPhasesRows
      ? expandedItemPhasesRows.map(enhanceWithRenderingData)
      : null,
    isFieldDataCollapsed: !item.isFieldDataCollapsible,
  };
};

type GetPhasesWithRenderingParametersArgs = {
  items: TimelineItemModel[];
  scaleX: TimeScalingFunction;
  visibleDomain: TimeRange;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  isModernizedStyle: boolean;
};
export const getPhasesWithRenderingParameters = ({
  items,
  scaleX,
  visibleDomain,
  viewRelevantComponentsScopeData,
  isModernizedStyle,
}: GetPhasesWithRenderingParametersArgs): TimelinePhasesWithRenderingParameters[] =>
  items.map((item, index, items) =>
    buildItemPhasesRenderingData({
      items,
      item,
      index,
      visibleDomain,
      scaleX,
      viewRelevantComponentsScopeData,
      isModernizedStyle,
    })
  );

export const getEventTargetEntityId = (
  e: React.MouseEvent,
  datasetAttributeName: string
): string | null | undefined => {
  if (!datasetAttributeName) {
    return;
  }

  const target = e.target as HTMLElement;
  if (!target || target.closest('.expander')) {
    return;
  }

  return target
    .closest(`[${datasetAttributeName}]`)
    ?.getAttribute(datasetAttributeName);
};
